import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PopupContext } from 'components';
import { useContext } from 'react';
import { getPageParamFromLastPage } from 'utils/getPageParamFromLastPage';
import PRODUCTS_KEYS from '../products.keys';
import PRODUCTS_MUTATIONS from '../products.mutation';
import PRODUCTS_QUERIES from '../products.queries';

export const useFetchProductSizes = ({ page, pageSize, search, ordering, filters }) =>
  useQuery({
    queryKey: PRODUCTS_KEYS.PRODUCT_SIZES_PAGINATED({
      page,
      pageSize,
      search,
      ordering,
      filters,
    }),
    queryFn: () =>
      PRODUCTS_QUERIES.FETCH_PRODUCT_SIZES({
        page,
        pageSize,
        search,
        ordering,
        filters,
      }),
    staleTime: 1 * 60 * 1000, // 1 minute
  });

export const useFetchProductSizesInfinite = ({ pageSize, search, ordering, filters }) =>
  useInfiniteQuery({
    queryKey: PRODUCTS_KEYS.PRODUCT_SIZES_INFINITE({ pageSize, filters, search, ordering }),
    queryFn: ({ pageParam = 1 }) =>
      PRODUCTS_QUERIES.FETCH_PRODUCT_SIZES({
        page: pageParam,
        pageSize,
        search,
        ordering,
        filters,
      }),
    getNextPageParam: (lastPage) => getPageParamFromLastPage(lastPage),
    staleTime: 1 * 60 * 1000, // 1 minute
  });

export const useCreateSize = () => {
  const queryClient = useQueryClient();
  const { closePopup } = useContext(PopupContext);
  return useMutation({
    mutationFn: PRODUCTS_MUTATIONS.CREATE_SIZE,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PRODUCTS_KEYS.PRODUCT_SIZES() });
      closePopup?.();
    },
  });
};

export const useUpdateSize = () => {
  const queryClient = useQueryClient();
  const { closePopup } = useContext(PopupContext);
  return useMutation({
    mutationFn: PRODUCTS_MUTATIONS.UPDATE_SIZE,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PRODUCTS_KEYS.PRODUCT_SIZES() });
      closePopup?.();
    },
  });
};

export const useDeleteSize = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: PRODUCTS_MUTATIONS.DELETE_SIZE,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PRODUCTS_KEYS.PRODUCT_SIZES() });
    },
  });
};

export const useGenerateSizes = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: PRODUCTS_MUTATIONS.GENERATE_SIZES,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PRODUCTS_KEYS.PRODUCT_SIZES() });
    },
  });
};
